import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { LanguageService } from './language.service';

/**
 * Standardized API response format from the backend.
 * All API endpoints return this wrapper.
 */
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  errors?: Record<string, string[]>;
  locked_until?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl || 'http://localhost:8000/api';
  private languageService = inject(LanguageService);

  constructor(private http: HttpClient) {}

  /**
   * Get default headers with authentication token
   */
  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Accept-Language': this.languageService.getCurrentLanguage()
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * GET request - returns the full ApiResponse wrapper
   */
  get<T = any>(endpoint: string): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, {
      headers: this.getHeaders()
    });
  }

  /**
   * POST request - returns the full ApiResponse wrapper
   */
  post<T, D extends object = Record<string, unknown>>(endpoint: string, data: D): Observable<ApiResponse<T>> {
    return this.http.post<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    });
  }

  /**
   * PUT request - returns the full ApiResponse wrapper
   */
  put<T, D extends object = Record<string, unknown>>(endpoint: string, data: D): Observable<ApiResponse<T>> {
    return this.http.put<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    });
  }

  /**
   * PATCH request - returns the full ApiResponse wrapper
   */
  patch<T, D extends object = Record<string, unknown>>(endpoint: string, data: D): Observable<ApiResponse<T>> {
    return this.http.patch<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    });
  }

  /**
   * DELETE request - returns the full ApiResponse wrapper
   */
  delete<T = any>(endpoint: string, options?: { body?: unknown }): Observable<ApiResponse<T>> {
    return this.http.delete<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, {
      headers: this.getHeaders(),
      body: options?.body
    });
  }

  /**
   * Upload file
   */
  uploadFile<T = any>(endpoint: string, file: File, additionalData?: Record<string, string>): Observable<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });
    }

    const token = localStorage.getItem('auth_token');
    let headers = new HttpHeaders({
      'Accept': 'application/json',
      'Accept-Language': this.languageService.getCurrentLanguage()
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return this.http.post<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, formData, {
      headers: headers
    });
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  /**
   * Remove authentication token
   */
  removeAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  /**
   * Check if user has valid token
   */
  hasAuthToken(): boolean {
    return !!localStorage.getItem('auth_token');
  }
}
